import { useState, useCallback } from 'react';

/**
 * Custom Hook für Smart Eventbrite Nearby Events
 * - Lazy Loading: Nur auf request
 * - Caching: 1 Tag im state; invalidiert bei Positions‑/Radiusänderung
 * - Parameter‑Key zur Vermeidung von veralteten Ergebnissen
 * - Fehler werden zurückgegeben zur Anzeige als Toast
 * - Intelligent Filtering: Nach User's Sport + Nähe
 */
export const useEventbriteNearby = (api) => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheTimestamp, setCacheTimestamp] = useState(null);
  const [cacheKey, setCacheKey] = useState(null); // store last query parameters

  const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 day

  const makeKey = (lat, lon, radius, sportKeys) => {
    const r = Number(radius) || 0;
    const s = (sportKeys || []).slice().sort().join(',');
    return `${lat.toFixed(5)}|${lon.toFixed(5)}|${r}|${s}`;
  };

  const isCacheValid = useCallback((key) => {
    if (!cacheTimestamp || !cacheKey || cacheKey !== key) return false;
    const ageMs = Date.now() - cacheTimestamp;
    return ageMs < CACHE_TTL_MS;
  }, [cacheTimestamp, cacheKey]);

  const fetchNearbyEvents = useCallback(
    async (lat, lon, radius = 10, sportKeys = []) => {
      // basic validation
      if (lat == null || lon == null) {
        const msg = '[EventBrite] Missing location parameters';
        console.warn(msg);
        setError(msg);
        return [];
      }
      const r = Number(radius) || 10;
      const key = makeKey(lat, lon, r, sportKeys);

      // Cache check (coordinates/radius change invalidates)
      if (isCacheValid(key) && events.length > 0) {
        console.log('[EventBrite] Using cached events');
        return events;
      }

      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          lat: lat.toString(),
          lon: lon.toString(),
          radius: r.toString(),
          sports: sportKeys.join(',')
        });

        const res = await api.get(`/api/events/nearby/eventbrite?${params}`);
        
        const fetchedEvents = res.data.events || [];
        setEvents(fetchedEvents);
        setCacheTimestamp(Date.now());
        setCacheKey(key);
        
        console.log(
          `[EventBrite] Loaded ${fetchedEvents.length} nearby events ` +
          `@ ${lat.toFixed(2)},${lon.toFixed(2)}`
        );
        
        return fetchedEvents;
      } catch (err) {
        console.error('[EventBrite] Fetch error:', err.message);
        setError(err.message || 'Could not load nearby events');
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [api, isCacheValid, events.length]
  );

  const clearCache = useCallback(() => {
    setEvents([]);
    setCacheTimestamp(null);
    setCacheKey(null);
    setError(null);
  }, []);

  return {
    events,
    isLoading,
    error,
    fetchNearbyEvents,
    clearCache,
    /**
     * check cache validity for a given set of parameters
     * @param {number} lat
     * @param {number} lon
     * @param {number} radius
     * @param {Array<string>} sportKeys
     */
    isCached: (lat, lon, radius = 10, sportKeys = []) => {
      const key = makeKey(lat, lon, radius, sportKeys);
      return isCacheValid(key);
    }
  };
};
